import { useEffect, useState } from 'react';
import type { StoredFile } from '@epasu/core';
import { crm, type FollowupDraftDto, type FollowupRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextArea } from '@/components/RichTextArea';
import { AnhangWahl } from '@/components/ReplyDialog';
import { PostfachWahl, useAktivePostfaecher } from '@/components/PostfachWahl';

interface Props {
  zeile: FollowupRow;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Nachfassen zu einer Erstansprache.
 *
 * Nahe am `ReplyDialog` gebaut, weil es dasselbe ist: eine Antwort im
 * bestehenden Verlauf. Der Unterschied steht am Anfang — Betreff und Text
 * kommen nicht leer, sondern als Entwurf vom Server, mit der Vorlage der
 * nächsten Stufe und der zitierten Originalmail darin. Was hier steht, geht
 * raus; wer die Vorlage nicht mag, schreibt darüber.
 */
export function FollowupDialog({ zeile, onClose, onSent }: Props) {
  const [entwurf, setEntwurf] = useState<FollowupDraftDto | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bibliothek, setBibliothek] = useState<StoredFile[]>([]);
  const [anhaenge, setAnhaenge] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Aus welchem Postfach die Erstansprache rausging, steht am Entwurf — von dort
  // wird nachgefasst, sonst landet das Nachfassen beim Empfänger nicht im selben
  // Verlauf. Der Entwurf kommt nachgeladen; bis dahin steht das Standardkonto da.
  // Bliebe die Auswahl leer, käme dasselbe heraus: ohne `accountId` nimmt der
  // Server von sich aus das Konto der Erstansprache.
  const absender = useAktivePostfaecher(entwurf?.original.accountId);

  useEffect(() => {
    let abgebrochen = false;
    void crm.followups.draft(zeile.messageId).then(
      (d) => {
        if (abgebrochen || !d) {
          if (!abgebrochen) setError('Zu dieser Nachricht gibt es keinen Entwurf mehr.');
          return;
        }
        setEntwurf(d);
        setSubject(d.subject);
        setBody(d.body);
      },
      (err: unknown) =>
        setError(err instanceof Error ? err.message : 'Entwurf konnte nicht geladen werden'),
    );
    return () => {
      abgebrochen = true;
    };
  }, [zeile.messageId]);

  useEffect(() => {
    void crm.files.list({ libraryOnly: true }).then(setBibliothek);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) {
      setError('Das Nachfassen ist leer.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await crm.followups.send({
        messageId: zeile.messageId,
        subject,
        body,
        ...(anhaenge.length ? { fileIds: anhaenge } : {}),
        ...(absender.konto ? { accountId: absender.konto } : {}),
      });
      if (!res.ok) {
        setError(res.error ?? 'Versand fehlgeschlagen');
        setBusy(false);
        return;
      }
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versand fehlgeschlagen');
      setBusy(false);
    }
  };

  const empfaenger = zeile.contactName ?? zeile.contactEmail ?? '—';

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Nachfassen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-2xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {entwurf ? `${entwurf.step}. Nachfassen` : 'Nachfassen'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          An {empfaenger}
          {zeile.contactEmail && zeile.contactName ? ` · ${zeile.contactEmail}` : ''}
        </p>

        {!entwurf && !error ? (
          <p className="mt-6 text-sm text-muted-foreground">Lade Entwurf …</p>
        ) : (
          <div className="mt-4 space-y-3">
            {entwurf && (
              <p className="text-xs text-muted-foreground">
                {entwurf.templateName
                  ? `Vorlage: ${entwurf.templateName}`
                  : 'Für diese Stufe gibt es keine Vorlage — der Text ist bis auf das Zitat leer.'}
              </p>
            )}

            <PostfachWahl
              konten={absender.konten}
              wert={absender.konto}
              onChange={absender.setKonto}
            />

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Betreff</span>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </label>

            {bibliothek.length > 0 && (
              <AnhangWahl dateien={bibliothek} gewaehlt={anhaenge} onChange={setAnhaenge} />
            )}

            <RichTextArea label="Text" value={body} onChange={setBody} />
            <p className="text-xs text-muted-foreground">
              Geht als Antwort im bestehenden Verlauf raus — beim Empfänger landet es
              unter der ursprünglichen Mail.
            </p>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy || !entwurf}>
            {busy ? 'Sende …' : 'Senden'}
          </Button>
        </div>
      </form>
    </div>
  );
}
